import React from 'react';
import { WeezHeader, Input, Form, SplitContainer, TranslateDropdown, FrameCard, SelectHierarchy, Button, Icon, Message, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n/i18n';
import { useHistory } from 'react-router-dom';
import { requiredField, useForm } from '../../../utils/validators';
import StyleJSON from '../../../components/json-editor/jsonEditor.module.css';
import Style from './designForm.module.css';
import TwigError from './components/twigError';

import DesignTemplateModel, { templateTypes } from '../../../models/designTemplateModel';
import RatesTreeModel from '../../../models/ratesTreeModel';
import DesignModel from '../../../models/designModel';
import EventModel from '../../../models/eventModel';

import { typesKeyNames, groupTypes } from '../list/components/helper';
import LoadingCard from '../../../components/loadingCard';
import GalleryModal from '../../../components/gallery';
import useAppContext from '../../../context/useAppContext';
import PreviewForm, { PreviewImage } from './components/previewForm';

import { defaultConfig, mergeConfig, splitConfig, onValidateConfig, accountLanguages } from './configUtils';
import { LANGUAGES } from '../../../components/translations/translationModal';

import JsonEditorReact from '../../../components/json-editor/jsonEditor';

export function splitTemplatesBaseCustom(templates, form, t) {
    if (!templates) return [];
    const foundTemplates = templates.filter(t => t?.type === form?.type);
    if (!foundTemplates) return [];
    const sortByName = (a, b) => {
        return a.text.localeCompare(b.text);
    };
    const makeOption = (template, parent) => {
        return {
            key: template.pk,
            value: template.pk,
            text: translateName(template?.translations?.name, template.name, i18n.accountLocale),
            template: template,
            parent: parent
        };
    };
    const levels = [
        { t_filter: t => t?.available_to_all_organizations, level: 'base' },
        { t_filter: t => !t?.available_to_all_organizations, level: 'custom' }
    ];
    const finalTemplatesOptions = [];
    levels.forEach(l => {
        const templatesInLevel = foundTemplates.filter(l.t_filter).map(t => makeOption(t, l.level));
        // we only add a group if there is a template inside
        if (templatesInLevel.length > 0)
            finalTemplatesOptions.push({
                value: l.level,
                text: t(`ticket.ticket_design.templates.${l.level}`),
                children: templatesInLevel.sort(sortByName)
            });
    });
    return finalTemplatesOptions;
}

function formatConfigForPreview(currentConfig, previewBackground) {
    const newConfig = {
        default: currentConfig?.fields
            ? Object.entries(currentConfig.fields).reduce((configFields, [field, value]) => {
                  configFields[field] = value.default_value;
                  return configFields;
              }, {})
            : {}
    };
    if (previewBackground) {
        newConfig.default.background = previewBackground;
    }

    return newConfig;
}

export function translateName(translations, defaultName, lang) {
    lang = lang.toLowerCase();
    if (translations && Object.keys(translations).includes(lang)) return translations[lang];
    return defaultName;
}

function GeneralInformationForm({
    form,
    setForm,
    setFormObject,
    formErrors,
    templateId,
    templates,
    useBaseTemplate,
    reloadTemplatePreview,
    previewBackground = null,
    setPreviewSrc,
    setUseBaseTemplate,
    toggleValidConfig,
    toggleValidVariables,
    forceUpdateEditors
}) {
    const { t } = useTranslation();

    const [updated, setUpdated] = React.useState(false);
    const [currentLanguage, setCurrentLanguage] = React.useState('default');
    const languagesOptions = React.useMemo(
        () =>
            accountLanguages
                .concat(['default'])
                .filter(lang => lang === 'default' || Object.keys(LANGUAGES).includes(lang))
                .map(lang => {
                    return { value: lang, label: lang === 'default' ? t('ticket.overview.defaultLanguage') : LANGUAGES[lang] };
                }),
        [accountLanguages, LANGUAGES]
    );

    const onUseTemplate = React.useCallback(
        selectedOption => {
            let templateSelected = selectedOption ? selectedOption.template.object : null;
            if (templateSelected) {
                templateSelected.config = mergeConfig(templateSelected);
                // for PDF types we use the preview form background if present
                const backgroundToUse = [templateTypes.PDF_TICKET, templateTypes.PDF_BADGE].includes(templateSelected.type) ? previewBackground : null;
                const previewConfig = formatConfigForPreview(templateSelected.config, backgroundToUse);
                reloadTemplatePreview({ config: previewConfig, code: templateSelected.code, type: templateSelected.type });
            } else {
                setPreviewSrc(null);
            }
            setUseBaseTemplate(templateSelected);
            setFormObject({ code: templateSelected?.code || {}, config: templateSelected?.config || defaultConfig }, true);
            forceUpdateEditors(templateSelected?.code || {}, templateSelected?.config || defaultConfig);
            toggleValidConfig(true);
            toggleValidVariables(true);
        },
        [setFormObject, reloadTemplatePreview, previewBackground, forceUpdateEditors]
    );

    const onFormChange = React.useCallback(
        (selected, id) => {
            if (id === 'name') setUpdated(true);
            setForm(id, selected);
        },
        [setForm]
    );

    const onChangeTranslation = React.useCallback(
        (newValue, lang) => {
            if (lang === 'default') return onFormChange(newValue, 'name');
            const newTranslations = form.translationsname || {};
            newTranslations[lang] = newValue || '';
            setForm('translationsname', newTranslations);
        },
        [setForm, form, currentLanguage]
    );

    const onChangeType = React.useCallback(
        selected => {
            if (!templateId) {
                setFormObject({ type: selected, code: {}, config: defaultConfig }, true);
                forceUpdateEditors({}, defaultConfig);
                setUseBaseTemplate(null);
                toggleValidConfig(true);
                toggleValidVariables(true);
                setPreviewSrc(null);
            }
        },
        [setFormObject, templateId, forceUpdateEditors]
    );

    const typeOptions = Object.entries(groupTypes).map(([group, types]) => {
        return {
            value: group,
            text: t(`ticket.ticket_design.group_type.${group}`),
            children: types?.map(type => {
                const [key, value] = Object.entries(typesKeyNames).find(([key, value]) => Number(key) === type);
                return {
                    key: key,
                    name: key,
                    value: Number(key),
                    text: t(`ticket.ticket_design.type.${value}`),
                    parent: group
                };
            })
        };
    });

    const templatesOptions = React.useMemo(() => splitTemplatesBaseCustom(templates, form, t), [templates, form, t]);

    return (
        <FrameCard title={t('ticket.ticket_design.create_template.information')}>
            <Form.Field>
                <Form.Label className={Style['translate-field-label']}>
                    {t('ticket.ticket_design.templates.name')}
                    <TranslateDropdown
                        defaultValue="default"
                        languages={languagesOptions}
                        onChange={value => setCurrentLanguage(value)}
                        selected={
                            form.translationsname
                                ? Object.entries(form.translationsname)
                                      .map(([l, v]) => (v ? l : undefined))
                                      .filter(e => e)
                                      .concat(form?.name ? ['default'] : null)
                                : []
                        }
                    />
                </Form.Label>
                <Input
                    errors={Boolean(updated && formErrors['name'])}
                    placeholder={t('ticket.ticket_design.create_template.name_placeholder')}
                    value={currentLanguage === 'default' ? form.name : form.translationsname ? form.translationsname[currentLanguage] || '' : ''}
                    maxLength={60}
                    onChange={e => onChangeTranslation(e.target.value, currentLanguage)}
                />
                <Form.FieldErrors errors={[updated && formErrors['name'] && t(formErrors['name'])]} />
            </Form.Field>
            <Form.Field>
                <Form.Label>{t('ticket.ticket_design.templates.format')}</Form.Label>
                <SelectHierarchy
                    widthIndent="10px"
                    errors={Boolean(formErrors['type'])}
                    disabled={Boolean(templateId)}
                    placeholder={t('ticket.ticket_design.create_design.placeholder_type')}
                    options={typeOptions}
                    value={form?.type || ''}
                    onChange={onChangeType}
                    onClear={templateId ? null : () => onChangeType(null)}
                />
                <Form.FieldErrors errors={formErrors['type'] && [t(formErrors['type'])]} />
            </Form.Field>
            {!templateId && (
                <Form.Field className={Style['search-hierarchy']}>
                    <Form.Label optionalText={t('ticket.common.labelOptional')}>{t('ticket.ticket_design.create_template.base')}</Form.Label>
                    <SelectHierarchy
                        widthIndent="10px"
                        disabled={Boolean(!form?.type)}
                        options={templatesOptions}
                        placeholder={t('ticket.ticket_design.create_design.placeholder_template_base')}
                        value={useBaseTemplate ? useBaseTemplate.id : null}
                        onChange={(_, selectedOption) => onUseTemplate(selectedOption)}
                        onClear={form?.type ? () => onUseTemplate(null) : null}
                    />
                </Form.Field>
            )}
            {form?.type === 8 && (
                <Message title={t('ticket.ticket_design.create_template.message.pvc_title')} type="default">
                    {t('ticket.ticket_design.create_template.message.pvc_description')}
                </Message>
            )}
        </FrameCard>
    );
}

function ConfigComponent({ form, setCanSubmit, setForm, useBaseTemplate, onChangeEditor, forceUpdateEditorConfig }) {
    const { t } = useTranslation();

    const resetForm = React.useCallback(
        (useDefault = false) => {
            forceUpdateEditorConfig(useDefault ? defaultConfig : useBaseTemplate?.config || defaultConfig);
            setForm('config', useDefault ? defaultConfig : useBaseTemplate?.config || defaultConfig);
        },
        [forceUpdateEditorConfig, useBaseTemplate]
    );

    return (
        <FrameCard title={t('ticket.ticket_design.create_template.options.title')} subtitle={t('ticket.ticket_design.create_template.options.description')}>
            <div className={StyleJSON['jsoneditor-react-container']}>
                <JsonEditorReact
                    id="config-editor"
                    setIsValid={setCanSubmit}
                    onValidate={json => onValidateConfig(json, t)}
                    setEditor={onChangeEditor}
                    mode="code"
                    json={form.config}
                    onChange={value => setForm('config', value)}
                />
            </div>
            <Button.Group>
                <Button onClick={() => resetForm()} icon={<Icon name={'Reset'} size="regular" />}>
                    {t('ticket.common.action.reset')}
                </Button>
                <Button onClick={() => resetForm(true)} icon={<Icon name={'cross-circle'} size="regular" />}>
                    {t('ticket.ticket_design.create_template.empty_form')}
                </Button>
            </Button.Group>
        </FrameCard>
    );
}

function VariablesComponent({ form, formErrors, setCanSubmit, setForm, useBaseTemplate = {}, codeTwigErrors = [], editor, onChangeEditor, forceUpdateEditorCode }) {
    const { t } = useTranslation();
    const [context] = useAppContext();
    const { organization } = context;

    const [openGallery, setOpenGallery] = React.useState(false);
    const [updated, setUpdated] = React.useState(false);

    const resetForm = React.useCallback(() => {
        forceUpdateEditorCode(useBaseTemplate?.code || {});
        setForm('code', useBaseTemplate?.code || {});
    }, [forceUpdateEditorCode, useBaseTemplate]);

    const appendImageToJSON = React.useCallback(
        imageUrl => {
            setOpenGallery(false);
            var customJson = editor.get();
            var field = { type: 'image', url: imageUrl, width: 20, height: 20, ratio: true, x: 0, y: 0 };
            if ([templateTypes.PDF_TICKET, templateTypes.PDF_BADGE].includes(form.type)) {
                field = { type: 'image', url: imageUrl, width: 20, x: 0, y: 0 };
            }
            if (!Object.keys(customJson)?.includes('fields')) customJson['fields'] = [];
            customJson['fields'].push(field);
            // we call the json editor instance set method
            editor.set(customJson);
            setForm('code', customJson);
        },
        [editor, form]
    );

    return (
        <FrameCard title={t('ticket.ticket_design.create_template.variables.title')} subtitle={t('ticket.ticket_design.create_template.variables.description')}>
            <div className={StyleJSON['jsoneditor-react-container']}>
                <TwigError codeTwigErrors={codeTwigErrors} />
                <JsonEditorReact
                    id="variables-editor"
                    setIsValid={setCanSubmit}
                    setEditor={onChangeEditor}
                    mode="code"
                    json={form.code}
                    onChange={value => {
                        setUpdated(true);
                        setForm('code', value);
                    }}
                />
                <Form.FieldErrors errors={updated && formErrors['code'] && [formErrors['code']]} />
            </div>
            <Button.Group>
                <Button primary inverted icon={<Icon name={'image'} />} onClick={() => setOpenGallery(true)}>
                    {t('ticket.ticket_design.create_template.upload')}
                </Button>
                <GalleryModal
                    // apiKeyUnsplash={window.weezConfig.unsplash}
                    onSelected={appendImageToJSON}
                    onUpload={appendImageToJSON}
                    organization={organization}
                    isOpenModal={openGallery}
                    onClose={() => setOpenGallery(false)}
                />
                <Button onClick={resetForm} icon={<Icon name={'Reset'} size="regular" />}>
                    {t('ticket.common.action.reset')}
                </Button>
            </Button.Group>
        </FrameCard>
    );
}

export default function TemplateForm({ organization, templateId = null }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [isValidCode, setIsValidCode] = React.useState(true);
    const [isValidConfig, setIsValidConfig] = React.useState(true);

    const [useBaseTemplate, setUseBaseTemplate] = React.useState(null);
    const [codeTwigErrors, setCodeTwigErrors] = React.useState([]);
    const [loadingPreview, setLoadingPreview] = React.useState(false);
    const [previewSrc, setPreviewSrc] = React.useState(null);
    const [ratesOptions, setRatesOptions] = React.useState([]);
    const [previewFormErrors, setPreviewFormErrors] = React.useState(null);

    const isCanSubmit = React.useMemo(() => isValidCode && isValidConfig, [isValidCode, isValidConfig]);

    const [templates, loadingTemplates] = DesignTemplateModel.useApiModel(
        {
            organizationId: organization.pk,
            launch: Boolean(!templateId),
            query: {
                include_shared_template: true
            }
        },
        [organization, templateId]
    );

    const [editorCode, setEditorCode] = React.useState(null);
    const [editorConfig, setEditorConfig] = React.useState(null);

    //JsonEditor is uncontrolled component, we pass by setter
    const forceUpdateEditorCode = React.useCallback(
        code => {
            editorCode?.set(code);
            editorCode?.resize(code);
        },
        [editorCode]
    );

    const forceUpdateEditorConfig = React.useCallback(
        config => {
            editorConfig?.set(config);
            editorConfig?.resize(config);
        },
        [editorConfig]
    );

    const forceUpdateEditors = React.useCallback(
        (code, config) => {
            forceUpdateEditorCode(code);
            forceUpdateEditorConfig(config);
        },
        [forceUpdateEditorCode, forceUpdateEditorConfig]
    );

    const updateValidationError = response => {
        if (!response) {
            setCodeTwigErrors([]);
            return;
        }
        // try catch in case JSON.parse fails
        try {
            const jsonResponse = JSON.parse(response);
            if (jsonResponse.errors?.length > 0) {
                const formattedErrors = jsonResponse.errors.map(error => {
                    let typeError = error?.error?.split('in') || null;
                    if (typeError && typeError[0]?.length > 0) {
                        error.error = typeError[0];
                    }
                    if (error?.content && error.content?.length > 0) {
                        let errorContent = error.content;
                        if (error.content?.length > 250) {
                            errorContent = error.content.substr(0, 250) + '...';
                        }
                        error.content = errorContent;
                    }
                    return error;
                });
                //scroll to json editor
                const variablesJsonEditor = document.querySelector('#error-container');
                variablesJsonEditor?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setCodeTwigErrors(formattedErrors);
            } else setCodeTwigErrors([]);
        } catch (e) {
            console.error(e);
        }
    };

    const validateCode = React.useCallback((name, code, form) => {
        if (code && Object.keys(code)?.length > 0) return true;
        return t('ticket.common.required');
    }, []);

    const [formState, setFormState, setFormObject, formErrors, formValidate] = useForm(
        {
            name: '',
            type: templateTypes.PDF_TICKET,
            code: {},
            config: defaultConfig,
            translationsname: {}
        },
        { name: requiredField, type: requiredField, code: validateCode }
    );

    const [previewFormState, setPreviewFormState, setPreviewFormObject] = useForm(
        {
            event_id: null,
            rate_id: null,
            barcode: null,
            attendee_id: null,
            background: null,
            language: organization?.language || 'fr-fr'
        },
        [organization]
    );

    const dataPreview = React.useMemo(() => {
        //we format the formState.config and add the previewFormState.background if present and if type is PDF
        const backgroundToUse = [templateTypes.PDF_TICKET, templateTypes.PDF_BADGE].includes(formState.type) ? previewFormState.background : null;
        return {
            code: formState.code || {},
            config: formatConfigForPreview(formState?.config, backgroundToUse) || { default: {} },
            is_download: false,
            type: formState.type || templateTypes.PDF_TICKET,
            language: previewFormState.language,
            event_id: previewFormState.event_id,
            rate_id: previewFormState.rate_id,
            attendee_id: null,
            barcode: previewFormState.barcode
        };
    }, [formState, previewFormState]);

    const reloadPreview = React.useCallback(
        (dataPreview, data = {}) => {
            const mergedData = { ...dataPreview, ...data };
            setLoadingPreview(true);
            setPreviewFormErrors(null);
            DesignModel.perform_preview({ data: mergedData }, { organization })
                .then(_src => {
                    updateValidationError(null);
                    setPreviewSrc(_src.response.preview);
                })
                .catch(_error => {
                    try {
                        if (_error.response) {
                            const errorObject = JSON.parse(_error.response);
                            if (errorObject && errorObject['barcode']) setPreviewFormErrors(errorObject);
                            updateValidationError(_error.response);
                        }
                    } catch {
                        console.error('JSON parse preview error');
                    }
                    toast.error(t('ticket.ticket_design.create_design.preview.error'));
                    setPreviewSrc(null);
                })
                .finally(() => setLoadingPreview(false));
        },
        [organization]
    );

    const [events, loadingEvents] = EventModel.useApiModel(
        {
            organizationId: organization.id
        },
        [organization, reloadPreview]
    );

    const [, loadingRates] = RatesTreeModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: previewFormState.event_id,
            //we check that the eventId isn't a key already in the allRates (to avoid requesting data we already have)
            launch: Boolean(organization && previewFormState?.event_id),
            onSuccess: _rates => {
                setRatesOptions(_rates.toOptions());
            }
        },
        [organization, previewFormState]
    );

    const [template, loadingTemplate] = DesignTemplateModel.useApiModel(
        {
            organizationId: organization.pk,
            id: templateId,
            launch: Boolean(templateId),
            onSuccess: _template => {
                //we add config (merging all config_form_... attributes in one variable)
                const templateForm = { ..._template.object, config: mergeConfig(_template.object), translationsname: _template.object?.translations?.name || {} };
                setFormObject(templateForm);
                setUseBaseTemplate(templateForm);
                reloadPreview({
                    event_id: null,
                    barcode: null,
                    attendee_id: null,
                    rate_id: null,
                    is_download: false,
                    config: formatConfigForPreview(templateForm.config, null),
                    code: templateForm.code,
                    type: templateForm.type,
                    language: 'fr-fr'
                });
            }
        },
        [organization, templateId, reloadPreview]
    );

    React.useEffect(() => {
        formValidate();
    }, [formState]);

    //if baseConfig used to had translations we send empty objects with the language key to clear them
    const clearEmptyLanguages = (config, baseConfig) => {
        const emptyConfig =
            baseConfig &&
            Object.keys(baseConfig).reduce((newConfig, key) => {
                if (key === 'descriptions' || key === 'labels') {
                    newConfig[key] = Object.keys(baseConfig[key]).reduce((langs, lang) => {
                        langs[lang] = {};
                        return langs;
                    }, {});
                }
                return newConfig;
            }, {});
        return (
            config &&
            Object.keys(config).reduce((newConfig, key) => {
                if (key !== 'fields') {
                    newConfig[key] = { ...emptyConfig[key], ...config[key] };
                } else {
                    newConfig[key] = config[key];
                }
                return newConfig;
            }, {})
        );
    };

    const onSubmit = React.useCallback(async () => {
        if (!formValidate() || !isCanSubmit) {
            toast.error(t('ticket.ticket_design.create_template.error'));
            return;
        }

        const validCode = await DesignTemplateModel.perform_validate({ type: formState.type.toString(), code: JSON.stringify(formState.code) }, { organization })
            .then(() => {
                updateValidationError(null);
                return true;
            })
            .catch(error => {
                if (error.response) updateValidationError(error.response);
                return false;
            });
        if (!validCode) return;
        const configOptions = splitConfig(clearEmptyLanguages(formState.config, useBaseTemplate?.config || {}));
        configOptions.translations.name = formState.translationsname;
        delete formState.translationsname;
        const template = new DesignTemplateModel({ ...formState, ...configOptions });
        template
            .save({ organizationId: organization.pk })
            .then(() => {
                toast.success(t('ticket.ticket_design.create_template.success'));
            })
            .catch(() => {
                toast.error(t('ticket.ticket_design.create_template.error'));
            })
            .finally(() => {
                history.push(prefix);
            });
    }, [formState, organization, isCanSubmit, useBaseTemplate]);

    const prefix = React.useMemo(() => {
        return `/ticket/O${organization.id}/tickets_design/templates`;
    }, [organization]);

    //const ITEMS = ['fr_FR', 'fr_CA', 'en_CA', 'en_US', 'en_GB', 'it_IT', 'es_ES'];

    return (
        <div>
            <WeezHeader
                backLink={{ onClick: () => history.push(prefix) }}
                title={t(`ticket.ticket_design.create_template.${templateId ? 'edit' : 'title'}`)}
                rightComponent={[
                    <Button key={0} disabled={!isCanSubmit || Object.keys(formErrors).length > 0} onClick={onSubmit} primary>
                        {t('ticket.common.action.save')}
                    </Button>
                ]}
            />
            <LoadingCard loading={loadingTemplate && loadingTemplates}>
                <SplitContainer
                    className={Style['split-container']}
                    size="maxi"
                    additional={
                        <PreviewImage
                            disabled={!dataPreview.code || Object.keys(dataPreview.code).length === 0}
                            previewSrc={previewSrc}
                            loadingPreview={loadingPreview}
                            reloadPreview={() => reloadPreview(dataPreview)}
                            templateType={dataPreview.type}
                        />
                    }
                >
                    <GeneralInformationForm
                        toggleValidConfig={setIsValidConfig}
                        toggleValidVariables={setIsValidCode}
                        form={formState}
                        templates={templateId ? [template] : templates}
                        setForm={setFormState}
                        setFormObject={setFormObject}
                        useBaseTemplate={useBaseTemplate}
                        setUseBaseTemplate={setUseBaseTemplate}
                        previewBackground={previewFormState.background}
                        reloadTemplatePreview={data => reloadPreview(dataPreview, data)}
                        setPreviewSrc={setPreviewSrc}
                        formErrors={formErrors}
                        templateId={templateId}
                        forceUpdateEditors={forceUpdateEditors}
                    />
                    <PreviewForm
                        isTemplate
                        organization={organization}
                        events={events}
                        loadingEvents={loadingEvents}
                        loadingRates={loadingRates}
                        rates={ratesOptions}
                        form={previewFormState}
                        setFormErrors={setPreviewFormErrors}
                        formErrors={previewFormErrors}
                        setForm={setPreviewFormState}
                        setFormObject={setPreviewFormObject}
                        dataPreview={dataPreview}
                        canDownload={dataPreview.code && Object.keys(dataPreview.code).length}
                        updateValidationError={updateValidationError}
                    />
                    <VariablesComponent
                        form={formState}
                        setCanSubmit={setIsValidCode}
                        setForm={setFormState}
                        formErrors={formErrors}
                        useBaseTemplate={useBaseTemplate}
                        codeTwigErrors={codeTwigErrors}
                        editor={editorCode}
                        onChangeEditor={setEditorCode}
                        forceUpdateEditorCode={forceUpdateEditorCode}
                    />
                    <ConfigComponent
                        form={formState}
                        setCanSubmit={setIsValidConfig}
                        setForm={setFormState}
                        useBaseTemplate={useBaseTemplate}
                        onChangeEditor={setEditorConfig}
                        forceUpdateEditorConfig={forceUpdateEditorConfig}
                    />
                </SplitContainer>
            </LoadingCard>
        </div>
    );
}
